import React, { useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertTriangle, BarChart3, CheckCircle2, ChevronRight, Clock3, Contact,
  FileText, Megaphone, MessageCircle, Plus, Send, Settings2, Users, Workflow,
  Activity, Eye, Link2, RefreshCw, Trash2, ShieldCheck, Webhook, X, Power, Save,
  Pencil, Copy, Tag, Image as ImageIcon, Link as LinkIcon, FlaskConical, Upload,
  Play, Pause, Square, ArrowLeft, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ImportModal from '../components/ImportModal';
import IconButton from '../components/IconButton';

const navItems = [
  { label: 'Visão geral', path: '/marketing', icon: BarChart3 },
  { label: 'Campanhas', path: '/marketing/campanhas', icon: Megaphone },
  { label: 'Contatos', path: '/marketing/contatos', icon: Contact },
  { label: 'Grupos', path: '/marketing/grupos', icon: Tag },
  { label: 'Mensagens', path: '/marketing/mensagens', icon: MessageCircle },
  { label: 'Relatórios', path: '/marketing/relatorios', icon: FileText },
  { label: 'Configurações', path: '/marketing/configuracoes', icon: Settings2 }
];

const flowSteps = [
  ['Entrada de contatos', Users, 'bg-blue-50 text-blue-600'],
  ['Validação', CheckCircle2, 'bg-emerald-50 text-emerald-600'],
  ['Segmentação', Contact, 'bg-violet-50 text-violet-600'],
  ['Escolha de mensagem', MessageCircle, 'bg-amber-50 text-amber-600'],
  ['Agendamento', Clock3, 'bg-cyan-50 text-cyan-600'],
  ['Envio WhatsApp', Send, 'bg-green-50 text-green-600'],
  ['Monitoramento', BarChart3, 'bg-indigo-50 text-indigo-600'],
  ['Finalização', CheckCircle2, 'bg-slate-100 text-slate-600']
];

const formatMinutes = (seconds, total) => `${Math.max(1, Math.ceil((seconds * total) / 60))} min`;

function Overview() {
  const [showFlow, setShowFlow] = useState(false);
  const metrics = [
    ['Contatos', '1.248', '+12,4%', Users, 'text-blue-600 bg-blue-50'],
    ['Campanhas', '18', '6 em execução', Megaphone, 'text-violet-600 bg-violet-50'],
    ['Taxa de entrega', '94,8%', '+3,1%', CheckCircle2, 'text-emerald-600 bg-emerald-50'],
    ['Mensagens enviadas', '8.432', 'neste mês', Send, 'text-amber-600 bg-amber-50']
  ];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><p className="text-xs font-bold tracking-widest text-primary-600">MARKETING</p><h1 className="text-2xl font-bold text-gray-900 dark:text-white">Campaign Manager</h1><p className="mt-1 text-sm text-gray-500">Gerencie suas campanhas de WhatsApp em um único lugar.</p></div>
        <Link to="/marketing/campanhas" className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"><Plus className="h-4 w-4" /> Nova campanha</Link>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">{metrics.map(([label, value, note, Icon, color]) => <div className="card p-5" key={label}><div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span><span className={`rounded-lg p-2 ${color}`}><Icon className="h-5 w-5" /></span></div><p className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">{value}</p><p className="mt-1 text-xs text-emerald-600">{note}</p></div>)}</div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="card p-5 xl:col-span-3"><div className="mb-4 flex items-center justify-between"><div><h2 className="font-semibold text-gray-900 dark:text-white">Evolução de envios</h2><p className="text-xs text-gray-500">Mensagens processadas nos últimos 7 dias</p></div><BarChart3 className="h-5 w-5 text-primary-600" /></div><div className="flex h-48 items-end gap-3 border-b border-l border-gray-100 px-4 pb-0 pt-8">{[42, 62, 48, 78, 92, 36, 55].map((height, index) => <div className="flex flex-1 flex-col items-center gap-2" key={index}><div className="w-full rounded-t-md bg-primary-500 transition-all hover:bg-primary-700" style={{ height: `${height}%` }} /><span className="text-[10px] text-gray-400">{['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'][index]}</span></div>)}</div></div>
        <div className="card p-5 xl:col-span-2"><div className="flex items-center justify-between"><div><h2 className="font-semibold text-gray-900 dark:text-white">Fluxo da campanha</h2><p className="text-xs text-gray-500">Regras visuais de execução</p></div><Workflow className="h-5 w-5 text-violet-600" /></div><div className="mt-5 space-y-2">{flowSteps.slice(0, 5).map(([label, Icon, color], index) => <div className="flex items-center gap-2" key={label}><span className={`rounded-md p-1.5 ${color}`}><Icon className="h-3.5 w-3.5" /></span><span className="text-xs text-gray-600 dark:text-gray-300">{label}</span>{index < 4 && <ChevronRight className="ml-auto h-3 w-3 text-gray-300" />}</div>)}</div><button type="button" onClick={() => setShowFlow(!showFlow)} className="mt-4 text-xs font-semibold text-primary-600">{showFlow ? 'Ocultar fluxo' : 'Abrir construtor visual'}</button>{showFlow && <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-500">O construtor visual permite conectar etapas, validar regras e salvar versões do fluxo da campanha.</div>}</div>
      </div>
      <div className="card overflow-hidden"><div className="border-b border-gray-100 px-5 py-4"><h2 className="font-semibold text-gray-900 dark:text-white">Campanhas recentes</h2></div>{[['Convite Feira de Negócios', 'Em execução', '64%'], ['Pesquisa de satisfação CDL', 'Agendada', '0%'], ['Aniversariantes do mês', 'Concluída', '100%']].map(([name, state, progress]) => <div className="flex items-center gap-4 border-b border-gray-50 px-5 py-3 last:border-0" key={name}><div className="rounded-lg bg-primary-50 p-2 text-primary-600"><Megaphone className="h-4 w-4" /></div><span className="min-w-0 flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">{name}</span><span className="hidden text-xs text-gray-500 sm:block">{progress}</span><span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-600">{state}</span></div>)}</div>
    </div>
  );
}

function CampaignEditor({ campaign, onBack, onSaved }) {
  return <CampaignEditorForm campaign={campaign} onBack={onBack} onSaved={onSaved} />;
}

function SimpleModule({ title, description, icon: Icon, action }) {
  return <div className="space-y-6"><div className="flex items-center justify-between"><div><p className="text-xs font-bold tracking-widest text-primary-600">MARKETING</p><h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1><p className="mt-1 text-sm text-gray-500">{description}</p></div>{action && <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> {action}</button>}</div><div className="card flex min-h-64 flex-col items-center justify-center p-8 text-center"><span className="rounded-2xl bg-primary-50 p-4 text-primary-600"><Icon className="h-8 w-8" /></span><h2 className="mt-4 font-semibold text-gray-800 dark:text-gray-200">Módulo pronto para operação</h2><p className="mt-2 max-w-md text-sm text-gray-500">A estrutura de {title.toLowerCase()} está disponível no módulo Marketing e será conectada aos dados oficiais da API conforme as integrações forem configuradas.</p></div></div>;
}

const campaignStatusMeta = {
  draft: ['Rascunho', 'bg-slate-100 text-slate-700'],
  initialized: ['Inicializada', 'bg-blue-50 text-blue-700'],
  running: ['Em andamento', 'bg-emerald-50 text-emerald-700'],
  paused: ['Pausada', 'bg-amber-50 text-amber-700'],
  finished: ['Finalizada', 'bg-gray-100 text-gray-700'],
  failed: ['Com falhas', 'bg-red-50 text-red-700'],
  cancelled: ['Cancelada', 'bg-zinc-100 text-zinc-700']
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString('pt-BR') : '-';
const getTodayInputDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};
const hourOptions = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`);

function StatusBadge({ status }) {
  const [label, color] = campaignStatusMeta[status] || [status, 'bg-gray-100 text-gray-700'];
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${color}`}>{label}</span>;
}

function CampaignEditorForm({ campaign, onBack, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const templatesSelectRef = useRef(null);
  const initialTemplateIds = campaign?.config?.templateIds?.length
    ? campaign.config.templateIds
    : (campaign?.templateId ? [campaign.templateId] : []);
  const [form, setForm] = useState({
    name: campaign?.name || '',
    objective: campaign?.objective || '',
    groupId: campaign?.groupId ? String(campaign.groupId) : '',
    templateIds: initialTemplateIds,
    minInterval: campaign?.config?.minInterval || 30,
    maxInterval: campaign?.config?.maxInterval || 60,
    businessHours: campaign?.config?.businessHours || '08:00 às 18:00',
    maxMessagesPerBlock: campaign?.config?.maxMessagesPerBlock || 50,
    sendPauseMinutes: campaign?.config?.sendPauseMinutes || 60,
    startSendDate: campaign?.config?.startSendDate || getTodayInputDate(),
    startSendTime: campaign?.config?.startSendTime || '08:00'
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const [templatesResponse, groupsResponse] = await Promise.all([
          api.get('/marketing/message-templates'),
          api.get('/marketing/groups')
        ]);
        setTemplates(templatesResponse.data?.data || []);
        setGroups(groupsResponse.data?.data || []);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Não foi possível carregar as opções da campanha');
      }
    };
    loadOptions();
  }, []);

  React.useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (templatesSelectRef.current && !templatesSelectRef.current.contains(event.target)) {
        setTemplatesOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const selectedTemplates = templates.filter((template) => form.templateIds.includes(template.id));
  const toggleTemplate = (templateId) => {
    setForm((current) => ({
      ...current,
      templateIds: current.templateIds.includes(templateId)
        ? current.templateIds.filter((id) => id !== templateId)
        : [...current.templateIds, templateId]
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.templateIds.length) {
      toast.error('Selecione pelo menos um template da mensagem');
      return;
    }
    if (!form.groupId) {
      toast.error('Selecione o grupo da campanha');
      return;
    }
    if (Number(form.maxMessagesPerBlock) <= 0) {
      toast.error('Informe o máximo de mensagens por bloco');
      return;
    }
    if (Number(form.sendPauseMinutes) <= 0) {
      toast.error('Informe a pausa de envio em minutos');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        objective: form.objective,
        groupId: Number(form.groupId),
        templateIds: form.templateIds,
        config: {
          minInterval: Number(form.minInterval),
          maxInterval: Number(form.maxInterval),
          businessHours: form.businessHours,
          maxMessagesPerBlock: Number(form.maxMessagesPerBlock),
          sendPauseMinutes: Number(form.sendPauseMinutes),
          startSendDate: form.startSendDate,
          startSendTime: form.startSendTime,
          templateIds: form.templateIds
        }
      };
      if (campaign?.id) {
        await api.put(`/marketing/campaigns/${campaign.id}`, payload);
        toast.success('Campanha atualizada');
      } else {
        await api.post('/marketing/campaigns', payload);
        toast.success('Campanha criada');
      }
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível salvar a campanha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold tracking-widest text-primary-600">CAMPANHAS · EDITAR</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{campaign ? 'Editar campanha' : 'Nova campanha'}</h1>
          <p className="mt-1 text-sm text-gray-500">Configure a campanha usando um template criado em Marketing → Mensagens.</p>
        </div>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="card space-y-4 p-5">
          <h2 className="font-semibold">Identidade da campanha</h2>
          <label className="block text-sm font-medium text-gray-700">Nome
            <input className="input mt-1" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ex.: Convite para evento CDL" />
          </label>
          <label className="block text-sm font-medium text-gray-700">Objetivo
            <textarea className="input mt-1" rows="4" value={form.objective} onChange={(event) => setForm({ ...form, objective: event.target.value })} placeholder="Descreva o resultado esperado" />
          </label>
          <label className="block text-sm font-medium text-gray-700">Grupo
            <select className="input mt-1" required value={form.groupId} onChange={(event) => setForm({ ...form, groupId: event.target.value })}>
              <option value="">Selecione...</option>
              {groups.map((group) => <option key={group.id} value={group.id}>{group.descricao}</option>)}
            </select>
          </label>
          <div className="relative" ref={templatesSelectRef}>
            <label className="block text-sm font-medium text-gray-700">Templates da mensagem</label>
            <button
              type="button"
              className="input mt-1 flex min-h-11 items-center justify-between gap-2 text-left"
              onClick={() => setTemplatesOpen((open) => !open)}
            >
              <span className={selectedTemplates.length ? 'text-gray-900' : 'text-gray-400'}>
                {selectedTemplates.length
                  ? `${selectedTemplates.length} template(s) selecionado(s)`
                  : 'Selecione um ou mais templates...'}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${templatesOpen ? 'rotate-180' : ''}`} />
            </button>
            {templatesOpen && (
              <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                {templates.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-gray-500">Nenhum template encontrado.</p>
                ) : templates.map((template) => {
                  const checked = form.templateIds.includes(template.id);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-primary-50 ${checked ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                      onClick={() => toggleTemplate(template.id)}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? 'border-primary-600 bg-primary-600' : 'border-gray-300 bg-white'}`}>
                        {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
                      </span>
                      <span className="flex-1">{template.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {form.templateIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedTemplates.map((template) => (
                <span key={template.id} className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">{template.name}</span>
              ))}
            </div>
          )}
          <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700">A mensagem é criada e versionada exclusivamente em Marketing → Mensagens.</div>
        </div>

        <div className="card p-5">
          <div className="mb-4 flex items-center gap-2"><Clock3 className="h-5 w-5 text-primary-600" /><h2 className="font-semibold">Planejamento inteligente</h2></div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <label className="text-xs font-medium text-gray-600">Intervalo mínimo (s)
              <input className="input mt-1" type="number" min="1" value={form.minInterval} onChange={(event) => setForm({ ...form, minInterval: Number(event.target.value) })} />
            </label>
            <label className="text-xs font-medium text-gray-600">Intervalo máximo (s)
              <input className="input mt-1" type="number" min="1" value={form.maxInterval} onChange={(event) => setForm({ ...form, maxInterval: Number(event.target.value) })} />
            </label>
            <label className="text-xs font-medium text-gray-600">Horário comercial
              <select className="input mt-1" value={form.businessHours} onChange={(event) => setForm({ ...form, businessHours: event.target.value })}>
                <option>08:00 às 18:00</option>
                <option>09:00 às 17:00</option>
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600">Máximo (Mensagens)
              <input className="input mt-1" type="number" min="1" value={form.maxMessagesPerBlock} onChange={(event) => setForm({ ...form, maxMessagesPerBlock: Number(event.target.value) })} />
            </label>
            <label className="text-xs font-medium text-gray-600">Pausa (Envio)
              <input className="input mt-1" type="number" min="1" value={form.sendPauseMinutes} onChange={(event) => setForm({ ...form, sendPauseMinutes: Number(event.target.value) })} />
              <span className="mt-1 block text-[10px] font-normal text-gray-400">Em minutos</span>
            </label>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-xs font-medium text-gray-600">Data Inicio (Envio)
              <input className="input mt-1" type="date" value={form.startSendDate} onChange={(event) => setForm({ ...form, startSendDate: event.target.value })} />
            </label>
            <label className="text-xs font-medium text-gray-600">Hora (Inicio)
              <select className="input mt-1 h-40 overflow-y-auto" size={10} value={form.startSendTime} onChange={(event) => setForm({ ...form, startSendTime: event.target.value })}>
                {hourOptions.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
              </select>
            </label>
          </div>
          {Number(form.maxInterval) < Number(form.minInterval) && <p className="mt-3 flex items-center gap-2 text-xs text-red-600"><AlertTriangle className="h-4 w-4" /> O máximo deve ser maior ou igual ao mínimo.</p>}
          <p className="mt-3 text-xs text-gray-500">
            A campanha alterna os templates selecionados em sequência, inicia em {form.startSendDate} às {form.startSendTime}, envia até {Number(form.maxMessagesPerBlock) || 0} mensagem(ns) por bloco e pausa {Number(form.sendPauseMinutes) || 0} minuto(s) antes do próximo bloco.
          </p>
        </div>
      </div>

      <div className="card p-5">
        <div className="mb-4 flex items-center gap-2"><Workflow className="h-5 w-5 text-violet-600" /><h2 className="font-semibold">Fluxo da campanha</h2></div>
        <div className="flex flex-wrap items-center gap-2">{flowSteps.map(([label, Icon, color], index) => <React.Fragment key={label}><div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${color}`}><Icon className="h-4 w-4" />{label}</div>{index < flowSteps.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300" />}</React.Fragment>)}</div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? 'Salvando...' : 'Salvar campanha'}
        </button>
      </div>
    </form>
  );
}

function CampaignDetails({ campaign, onBack, onChanged }) {
  const [details, setDetails] = useState(null);

  const loadDetails = async () => {
    try {
      const response = await api.get(`/marketing/campaigns/${campaign.id}/details`);
      setDetails(response.data?.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar os detalhes');
    }
  };

  React.useEffect(() => {
    loadDetails();
    const timer = setInterval(loadDetails, 5000);
    return () => clearInterval(timer);
  }, [campaign.id]);

  const action = async (type) => {
    if (type === 'stop' && !window.confirm('Encerrar definitivamente a execução atual?')) return;
    try {
      await api.post(`/marketing/campaigns/${campaign.id}/${type}`);
      toast.success(type === 'start' ? 'Campanha iniciada' : type === 'pause' ? 'Campanha pausada' : 'Execução encerrada');
      await loadDetails();
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ação não realizada');
    }
  };

  const current = details?.campaign || campaign;
  const metrics = details?.metrics || { totalContacts: 0, sentCount: 0, pendingCount: 0, failedCount: 0, progress: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold tracking-widest text-primary-600">CAMPANHAS · DETALHES</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{current.name}</h1>
          <p className="mt-1 text-sm text-gray-500">Monitoramento e acompanhamento da campanha.</p>
        </div>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200">
          <ArrowLeft className="h-4 w-4" /> Voltar para campanhas
        </button>
      </div>

      <div className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900">Informações da campanha</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
              <span>Status: <StatusBadge status={current.status} /></span>
              <span>Início: <strong>{formatDateTime(current.startedAt)}</strong></span>
              <span>Última atualização: <strong>{formatDateTime(details?.updatedAt || current.updatedAt)}</strong></span>
              <span>Grupo: <strong>{current.group?.descricao || '-'}</strong></span>
              <span>Template: <strong>{current.template?.name || '-'}</strong></span>
              <span>Máximo por bloco: <strong>{current.config?.maxMessagesPerBlock || 50}</strong></span>
              <span>Pausa de envio: <strong>{current.config?.sendPauseMinutes || 60} min</strong></span>
              <span>Início envio: <strong>{current.config?.startSendDate || '-'} {current.config?.startSendTime || '08:00'}</strong></span>
            </div>
          </div>
          <div className="flex gap-2">
            {current.status !== 'running' && <IconButton icon={Play} onClick={() => action('start')} variant="success" title="Iniciar / Retomar" />}
            {current.status === 'running' && <IconButton icon={Pause} onClick={() => action('pause')} variant="warning" title="Pausar" />}
            <IconButton icon={Square} onClick={() => action('stop')} variant="danger" title="Stop" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {[
          ['Total de contatos', metrics.totalContacts],
          ['Mensagens enviadas', metrics.sentCount],
          ['Mensagens pendentes', metrics.pendingCount],
          ['Mensagens com falha', metrics.failedCount]
        ].map(([label, value]) => <div className="card p-5" key={label}><p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p><p className="mt-3 text-2xl font-bold text-gray-900">{value}</p></div>)}
      </div>

      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between"><h2 className="font-semibold text-gray-900">Progresso visual</h2><span className="text-sm font-semibold text-gray-600">{metrics.progress}% concluído</span></div>
        <div className="h-3 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${metrics.progress}%` }} /></div>
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
          <StatusBadge status={current.status} />
          <span>Atualização automática a cada 5 segundos</span>
        </div>
      </div>
    </div>
  );
}

function CampaignsModule() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list');
  const [selected, setSelected] = useState(null);

  const loadCampaigns = async () => {
    try {
      const response = await api.get('/marketing/campaigns');
      setCampaigns(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar as campanhas');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadCampaigns(); }, []);

  const action = async (campaign, type) => {
    try {
      await api.post(`/marketing/campaigns/${campaign.id}/${type}`);
      toast.success(type === 'start' ? 'Campanha iniciada' : 'Campanha pausada');
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ação não realizada');
    }
  };

  const edit = (campaign = null) => {
    if (campaign && ['running', 'paused'].includes(campaign.status)) {
      toast.error('Esta campanha possui uma execução ativa ou suspensa. Para editar suas configurações, acesse Detalhes e utilize o botão Stop para encerrar a execução atual.');
      return;
    }
    setSelected(campaign || null);
    setMode('edit');
  };

  if (mode === 'details' && selected) return <CampaignDetails campaign={selected} onBack={() => { setMode('list'); loadCampaigns(); }} onChanged={loadCampaigns} />;
  if (mode === 'edit') return <CampaignEditor campaign={selected} onBack={() => setMode('list')} onSaved={() => { setMode('list'); loadCampaigns(); }} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold tracking-widest text-primary-600">MARKETING · CAMPANHAS</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Campanhas</h1>
          <p className="mt-1 text-sm text-gray-500">Gerenciamento simplificado das campanhas.</p>
        </div>
        <IconButton icon={Plus} onClick={() => edit(null)} variant="primary" title="Nova campanha" />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nome da campanha', 'Status', 'Iniciar / Pausar', 'Detalhes', 'Editar'].map((header) => (
                  <th key={header} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-sm text-gray-500">Carregando campanhas...</td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-sm text-gray-500">Nenhuma campanha cadastrada.</td></tr>
              ) : campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm font-semibold text-gray-900">{campaign.name}</td>
                  <td className="px-5 py-4"><StatusBadge status={campaign.status} /></td>
                  <td className="px-5 py-4">
                    {campaign.status === 'running'
                      ? <IconButton icon={Pause} onClick={() => action(campaign, 'pause')} variant="warning" size="sm" title="Pausar" />
                      : <IconButton icon={Play} onClick={() => action(campaign, 'start')} variant="success" size="sm" title="Iniciar / Retomar" />}
                  </td>
                  <td className="px-5 py-4"><IconButton icon={BarChart3} onClick={() => { setSelected(campaign); setMode('details'); }} variant="info" size="sm" title="Detalhes" /></td>
                  <td className="px-5 py-4"><IconButton icon={Pencil} onClick={() => edit(campaign)} variant="warning" size="sm" title="Editar" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const normalizeBrazilPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.startsWith('55') ? digits : `55${digits}`;
};

const contactImportFields = [
  { key: 'nome', label: 'Nome', required: true },
  { key: 'celular', label: 'Celular', required: true, transform: normalizeBrazilPhone },
  { key: 'empresa', label: 'Empresa', required: false },
  { key: 'evento', label: 'Evento', required: false },
  { key: 'local', label: 'Local', required: false }
];

const groupColorPalette = [
  '#2563eb', '#16a34a', '#dc2626', '#f59e0b', '#7c3aed',
  '#0891b2', '#db2777', '#4b5563', '#0f766e', '#ea580c'
];

const isHexColor = (value) => /^#[0-9a-fA-F]{6}$/.test(String(value || ''));
const colorInputValue = (value) => (isHexColor(value) ? value : '#2563eb');

function MarketingContacts() {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);

  const loadContacts = async () => {
    try {
      const response = await api.get('/marketing/contacts', { params: search ? { search } : {} });
      setContacts(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar os contatos');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/marketing/groups');
      setGroups(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar os grupos');
    }
  };

  React.useEffect(() => {
    loadGroups();
    loadContacts();
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(loadContacts, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const importContacts = async (rows, extraValues = {}) => {
    const groupId = Number(extraValues.groupId);
    const eventDate = extraValues.data_evt;
    if (!groupId) {
      toast.error('Selecione o grupo antes de importar');
      return;
    }
    if (!eventDate) {
      toast.error('Selecione a Data Importação antes de importar');
      return;
    }

    try {
      const response = await api.post('/marketing/contacts/import', { rows, groupId, eventDate });
      toast.success(`${response.data?.count || 0} contato(s) importado(s) com sucesso`);
      setShowImport(false);
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível importar os contatos');
    }
  };

  const removeContact = async (contact) => {
    if (!window.confirm(`Excluir o contato "${contact.nome}"?`)) return;
    try {
      await api.delete(`/marketing/contacts/${contact.id}`);
      toast.success('Contato excluído');
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível excluir o contato');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-widest text-primary-600">MARKETING · CONTATOS</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contatos</h1>
          <p className="mt-1 text-sm text-gray-500">Importe os contatos da tabela importa_par e vincule todos a um grupo.</p>
        </div>
        <IconButton icon={Upload} onClick={() => setShowImport(true)} variant="success" title="Importar contatos" />
      </div>

      <div className="card p-5">
        <input
          className="input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por nome, celular, empresa, evento ou grupo..."
        />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['ID', 'Nome', 'Celular', 'Empresa', 'Evento', 'Data', 'Local', 'Grupo', 'Ações'].map((header) => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-sm text-gray-500">Carregando contatos...</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-sm text-gray-500">Nenhum contato encontrado.</td></tr>
              ) : contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{contact.nome || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.celular || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.empresa || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.evento || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.data_evt ? new Date(`${contact.data_evt}T00:00:00`).toLocaleDateString('pt-BR') : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.local || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{contact.grupo_part || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <IconButton icon={Trash2} onClick={() => removeContact(contact)} variant="danger" size="sm" title="Excluir contato" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={importContacts}
        fields={contactImportFields}
        extraFields={[{
          key: 'groupId',
          label: 'Grupo',
          required: true,
          options: groups.map((group) => ({ value: String(group.id), label: group.descricao }))
        }, {
          key: 'data_evt',
          label: 'Data Importação',
          type: 'date',
          required: true
        }]}
      />
    </div>
  );
}

function MarketingGroups() {
  const emptyForm = { descricao: '', etiqueta: '#2563eb', obs: '' };
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadGroups = async () => {
    try {
      const response = await api.get('/marketing/groups');
      setGroups(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar os grupos');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadGroups(); }, []);

  const startEdit = (group) => {
    setEditing(group);
    setForm({
      descricao: group.descricao || '',
      etiqueta: group.etiqueta || '',
      obs: group.obs || ''
    });
  };

  const resetForm = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const saveGroup = async (event) => {
    event.preventDefault();
    if (!form.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await api.put(`/marketing/groups/${editing.id}`, form);
        toast.success('Grupo atualizado');
      } else {
        await api.post('/marketing/groups', form);
        toast.success('Grupo criado');
      }
      resetForm();
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível salvar o grupo');
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (group) => {
    if (!window.confirm(`Excluir o grupo "${group.descricao}"?`)) return;
    try {
      await api.delete(`/marketing/groups/${group.id}`);
      toast.success('Grupo excluído');
      if (editing?.id === group.id) resetForm();
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível excluir o grupo');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold tracking-widest text-primary-600">MARKETING · GRUPOS</p>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Grupos</h1>
        <p className="mt-1 text-sm text-gray-500">Cadastre os grupos da tabela grupo_comunica usados na importação de contatos.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <form onSubmit={saveGroup} className="card space-y-4 p-5 xl:col-span-1">
          <h2 className="font-semibold text-gray-900">{editing ? 'Editar grupo' : 'Novo grupo'}</h2>
          <label className="block text-sm font-medium text-gray-700">Descrição *
            <input className="input mt-1" maxLength={182} required value={form.descricao} onChange={(event) => setForm({ ...form, descricao: event.target.value })} />
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700">Etiqueta</label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color"
                className="h-10 w-14 cursor-pointer rounded border border-gray-300 bg-white p-1"
                value={colorInputValue(form.etiqueta)}
                onChange={(event) => setForm({ ...form, etiqueta: event.target.value })}
                title="Escolher cor da etiqueta"
                aria-label="Escolher cor da etiqueta"
              />
              <input
                className="input"
                maxLength={40}
                value={form.etiqueta || ''}
                onChange={(event) => setForm({ ...form, etiqueta: event.target.value })}
                placeholder="#2563eb"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {groupColorPalette.map((color) => (
                <button
                  type="button"
                  key={color}
                  onClick={() => setForm({ ...form, etiqueta: color })}
                  className={`h-8 w-8 rounded-full border-2 ${form.etiqueta === color ? 'border-gray-900' : 'border-white'} shadow-sm ring-1 ring-gray-200`}
                  style={{ backgroundColor: color }}
                  title={color}
                  aria-label={`Selecionar cor ${color}`}
                />
              ))}
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">Observação
            <textarea className="input mt-1" rows="4" maxLength={40} value={form.obs} onChange={(event) => setForm({ ...form, obs: event.target.value })} />
          </label>
          <div className="flex justify-end gap-2">
            {editing && <IconButton icon={X} onClick={resetForm} variant="secondary" title="Cancelar" />}
            <IconButton icon={Save} type="submit" disabled={saving} variant="primary" title={saving ? 'Salvando...' : 'Salvar grupo'} />
          </div>
        </form>

        <div className="card overflow-hidden xl:col-span-2">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['ID', 'Descrição', 'Etiqueta', 'Observação', 'Ações'].map((header) => (
                    <th key={header} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">Carregando grupos...</td></tr>
                ) : groups.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">Nenhum grupo cadastrado.</td></tr>
                ) : groups.map((group) => {
                  const etiquetaValue = editing?.id === group.id ? form.etiqueta : group.etiqueta;
                  return (
                    <tr key={group.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{group.id}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{group.descricao || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {etiquetaValue ? (
                          <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-2.5 py-1 ring-1 ring-gray-200">
                            <span
                              className="h-5 w-5 rounded-full border border-gray-200 shadow-sm"
                              style={{ backgroundColor: colorInputValue(etiquetaValue) }}
                            />
                            <span className="font-medium">{etiquetaValue}</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{group.obs || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <IconButton icon={Pencil} onClick={() => startEdit(group)} variant="warning" size="sm" title="Editar grupo" />
                          <IconButton icon={Trash2} onClick={() => removeGroup(group)} variant="danger" size="sm" title="Excluir grupo" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageTemplates() {
  const emptyForm = { name: '', description: '', content: 'Olá {{nome}}, temos uma novidade para você!' };
  const messageRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connections, setConnections] = useState([]);
  const [showMedia, setShowMedia] = useState(null);
  const [mediaValue, setMediaValue] = useState('');
  const [showTest, setShowTest] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testConnection, setTestConnection] = useState('');
  const [testing, setTesting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/marketing/message-templates');
      setTemplates(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar os templates');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadTemplates(); }, []);

  const variables = useMemo(() => [...new Set(
    form.content.match(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g)?.map((item) => item.replace(/[{}]/g, '').trim()) || []
  )], [form.content]);

  const preview = form.content
    .replace(/\{\{\s*nome\s*\}\}/gi, 'Ana')
    .replace(/\{\{\s*empresa\s*\}\}/gi, 'CDL Itaúna')
    .replace(/\{\{\s*data\s*\}\}/gi, '15/11/2026')
    .replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_, variable) => `[${variable}]`);
  const previewLines = preview.split('\n');
  const mediaPreviewUrl = (value) => value.startsWith('/uploads/') ? `/api${value}` : value;
  const getLinkPreview = (url) => {
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'chat.whatsapp.com') {
        return {
          title: 'Fala comigo!',
          description: 'Grupo na comunidade "Convite para grupo do WhatsApp"',
          action: 'Entrar no grupo'
        };
      }
    } catch {
      return null;
    }
    return null;
  };
  const renderFormattedText = (text) => {
    const parts = [];
    const pattern = /(\*[^*\n]+\*|_[^_\n]+_)/g;
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      const token = match[0];
      const value = token.slice(1, -1);
      parts.push(token.startsWith('*') ? <strong key={`${match.index}-${token}`}>{value}</strong> : <em key={`${match.index}-${token}`}>{value}</em>);
      lastIndex = match.index + token.length;
    }

    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const renderPreviewLine = (line, index) => {
    const image = line.match(/^🖼️ Imagem:\s*((?:https?:\/\/|\/)\S+)/);
    const video = line.match(/^🎥 Vídeo:\s*((?:https?:\/\/|\/)\S+)/);
    const link = line.match(/^🔗\s*(https?:\/\/\S+)/);
    if (image) return <img className="mb-2 max-h-56 w-full rounded-lg object-cover" src={mediaPreviewUrl(image[1])} alt="Imagem do template" key={`${line}-${index}`} />;
    if (video) return <video className="mb-2 max-h-56 w-full rounded-lg bg-black" src={mediaPreviewUrl(video[1])} controls preload="metadata" key={`${line}-${index}`}>Seu navegador não suporta vídeo.</video>;
    if (link) {
      const previewData = getLinkPreview(link[1]);
      if (previewData) {
        return <div className="mb-2 overflow-hidden rounded-lg bg-white" key={`${line}-${index}`}><div className="flex gap-3 bg-gray-100 p-3"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold leading-3 text-white">SE<br />LIGA!</span><div className="min-w-0"><p className="font-semibold text-gray-900">{previewData.title}</p><p className="truncate text-xs text-gray-500">{previewData.description}</p><p className="truncate text-xs text-gray-500">{link[1]}</p></div></div><a className="block truncate px-2 py-2 font-semibold text-emerald-700 underline" href={link[1]} target="_blank" rel="noreferrer">{link[1]}</a><a className="block border-t border-gray-100 px-3 py-2 text-center font-semibold text-emerald-700" href={link[1]} target="_blank" rel="noreferrer">{previewData.action}</a></div>;
      }
      return <a className="mb-2 block overflow-hidden rounded-lg bg-white text-blue-700 underline" href={link[1]} target="_blank" rel="noreferrer" key={`${line}-${index}`}><span className="block bg-gray-100 px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500">Link</span><span className="block truncate px-3 py-2">{link[1]}</span></a>;
    }
    return <span className="block whitespace-pre-wrap" key={`${line}-${index}`}>{line ? renderFormattedText(line) : '\u00a0'}</span>;
  };

  const startNew = () => {
    setSelected(null);
    setForm(emptyForm);
  };

  const appendContent = (value) => {
    setForm((current) => ({ ...current, content: `${current.content}${current.content ? '\n' : ''}${value}` }));
  };

  const wrapSelectedContent = (before, after, placeholder) => {
    const textarea = messageRef.current;
    if (!textarea) {
      appendContent(`${before}${placeholder}${after}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = form.content.slice(start, end) || placeholder;
    const nextContent = `${form.content.slice(0, start)}${before}${selectedText}${after}${form.content.slice(end)}`;
    setForm((current) => ({ ...current, content: nextContent }));

    window.requestAnimationFrame(() => {
      textarea.focus();
      const cursorStart = start + before.length;
      const cursorEnd = cursorStart + selectedText.length;
      textarea.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const insertMedia = () => {
    if (!mediaValue.trim()) return;
    appendContent(showMedia === 'image' ? `🖼️ Imagem: ${mediaValue.trim()}` : `🔗 ${mediaValue.trim()}`);
    setMediaValue('');
    setShowMedia(null);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Selecione uma imagem ou vídeo válido');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('A imagem ou vídeo deve ter no máximo 20 MB');
      return;
    }
    const data = new FormData();
    data.append('image', file);
    setUploadingImage(true);
    try {
      const response = await api.post('/marketing/provider-connections/message-image', data, {
        headers: { 'Content-Type': undefined }
      });
      appendContent(file.type.startsWith('video/') ? `🎥 Vídeo: ${response.data.data.url}` : `🖼️ Imagem: ${response.data.data.url}`);
      setShowMedia(null);
      toast.success('Imagem enviada');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível enviar a imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const openTest = async () => {
    setShowTest(true);
    if (connections.length) return;
    try {
      const response = await api.get('/marketing/provider-connections');
      const rows = response.data?.data || [];
      setConnections(rows);
      setTestConnection(rows.find((connection) => connection.status === 'active')?.id || rows[0]?.id || '');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar as conexões');
    }
  };

  const sendTest = async (event) => {
    event.preventDefault();
    if (!testConnection || !testPhone.trim()) return;
    setTesting(true);
    try {
      await api.post(
        `/marketing/provider-connections/${testConnection}/test-message`,
        { phone: testPhone, content: preview },
        { timeout: 60000, skipGlobalErrorToast: true }
      );
      toast.success('Mensagem de teste enviada');
      setShowTest(false);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('O envio demorou para confirmar. Verifique no WhatsApp antes de reenviar.');
      } else {
        toast.error(error.response?.data?.message || 'Não foi possível enviar a mensagem de teste');
      }
    } finally {
      setTesting(false);
    }
  };

  const editTemplate = (template) => {
    const active = template.versions?.find((version) => version.isActive) || template.versions?.[0];
    setSelected(template);
    setForm({ name: template.name, description: template.description || '', content: active?.content || '' });
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const response = selected
        ? await api.put(`/marketing/message-templates/${selected.id}`, form)
        : await api.post('/marketing/message-templates', form);
      toast.success(selected ? 'Nova versão salva' : 'Template criado');
      setSelected(response.data?.data || null);
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível salvar o template');
    } finally {
      setSaving(false);
    }
  };

  const activateVersion = async (versionId) => {
    try {
      await api.put(`/marketing/message-templates/${selected.id}`, { activateVersion: versionId });
      toast.success('Versão ativada');
      const response = await api.get('/marketing/message-templates');
      const updated = response.data?.data?.find((item) => item.id === selected.id);
      setTemplates(response.data?.data || []);
      if (updated) editTemplate(updated);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível ativar a versão');
    }
  };

  const duplicateTemplate = (template) => {
    const active = template.versions?.find((version) => version.isActive) || template.versions?.[0];
    setSelected(null);
    setForm({ name: `${template.name} (cópia)`, description: template.description || '', content: active?.content || '' });
  };

  return <div className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><p className="text-xs font-bold tracking-widest text-primary-600">MARKETING · MENSAGENS</p><h1 className="text-2xl font-bold text-gray-900 dark:text-white">Templates de mensagens</h1><p className="mt-1 text-sm text-gray-500">Crie versões, variáveis e prévias para suas campanhas.</p></div>
      <button type="button" onClick={startNew} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Novo template</button>
    </div>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
      <div className="space-y-3 xl:col-span-2">
        {loading ? <div className="card p-5 text-sm text-gray-500">Carregando templates...</div> : templates.length === 0 ? <div className="card p-6 text-center text-sm text-gray-500">Nenhum template criado ainda.</div> : templates.map((template) => {
          const active = template.versions?.find((version) => version.isActive) || template.versions?.[0];
          return <div className={`card p-4 ${selected?.id === template.id ? 'border-primary-400 ring-1 ring-primary-200' : ''}`} key={template.id}>
            <div className="flex items-start justify-between gap-3"><button type="button" onClick={() => editTemplate(template)} className="min-w-0 text-left"><h2 className="truncate font-semibold text-gray-900 dark:text-white">{template.name}</h2><p className="mt-1 line-clamp-2 text-xs text-gray-500">{active?.content}</p></button><span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${template.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{template.status === 'active' ? 'Ativo' : 'Rascunho'}</span></div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500"><span>{template.versions?.length || 0} versão(ões)</span><button type="button" title="Duplicar template" aria-label="Duplicar template" onClick={() => duplicateTemplate(template)} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"><Copy className="h-4 w-4" /></button></div>
          </div>;
        })}
      </div>
      <form onSubmit={saveTemplate} className="card space-y-4 p-5 xl:col-span-3">
        <div className="flex items-center justify-between"><h2 className="font-semibold">{selected ? 'Editar template e criar versão' : 'Novo template'}</h2>{selected && <span className="text-xs text-gray-500">versão atual: {selected.versions?.find((version) => version.isActive)?.version || 1}</span>}</div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome<input className="input mt-1" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ex.: Convite para evento" /></label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descrição<input className="input mt-1" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Quando usar este template" /></label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mensagem<textarea ref={messageRef} className="input mt-1 min-h-72 resize-y" required rows="12" value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} /></label>
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
          <span className="mr-1 text-xs font-semibold text-gray-500">Inserir:</span>
          <button type="button" title="Inserir negrito do WhatsApp" aria-label="Inserir negrito do WhatsApp" className="rounded-md px-2 py-1 text-sm font-bold text-gray-700 hover:bg-white" onClick={() => wrapSelectedContent('*', '*', 'texto')}>B</button>
          <button type="button" title="Inserir itálico do WhatsApp" aria-label="Inserir itálico do WhatsApp" className="rounded-md px-2 py-1 text-sm italic text-gray-700 hover:bg-white" onClick={() => wrapSelectedContent('_', '_', 'texto')}>I</button>
          {['😀', '🎉', '📣', '✅', '❤️', '👉'].map((emoji) => <button type="button" title={`Inserir ${emoji}`} aria-label={`Inserir ${emoji}`} className="rounded-md px-2 py-1 text-lg hover:bg-white" key={emoji} onClick={() => appendContent(emoji)}>{emoji}</button>)}
          <button type="button" title="Inserir imagem ou vídeo" aria-label="Inserir imagem ou vídeo" className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-white" onClick={() => { setShowMedia('image'); setMediaValue(''); }}><ImageIcon className="h-4 w-4" />Imagem ou vídeo</button>
          <button type="button" title="Inserir link" aria-label="Inserir link" className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-white" onClick={() => { setShowMedia('link'); setMediaValue(''); }}><LinkIcon className="h-4 w-4" />Link</button>
        </div>
        {showMedia && <div className="flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 p-3">{showMedia === 'image' ? <><label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white">{uploadingImage ? 'Enviando...' : 'Escolher imagem ou vídeo'}<input className="hidden" type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg" disabled={uploadingImage} onChange={handleImageUpload} /></label><span className="text-xs text-gray-500">Imagem ou vídeo · máximo 20 MB</span></> : <><input className="input flex-1 bg-white" autoFocus type="url" value={mediaValue} onChange={(event) => setMediaValue(event.target.value)} placeholder="https://site.com/oferta" /><button type="button" onClick={insertMedia} className="rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white">Inserir link</button></>}<button type="button" onClick={() => setShowMedia(null)} className="rounded-lg px-2 text-gray-500">×</button></div>}
        <div><div className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-500"><Tag className="h-4 w-4" /> Variáveis detectadas</div><div className="flex flex-wrap gap-2">{variables.length ? variables.map((variable) => <span className="rounded-md bg-violet-50 px-2 py-1 text-xs text-violet-700" key={variable}>{`{{${variable}}}`}</span>) : <span className="text-xs text-gray-400">Digite variáveis como {'{{nome}}'}.</span>}</div></div>
        <div><p className="mb-2 text-xs font-semibold text-gray-500">Inserir variável</p><div className="flex flex-wrap gap-2">{['nome', 'empresa', 'telefone', 'evento', 'data', 'local'].map((variable) => <button type="button" className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600" key={variable} onClick={() => setForm({ ...form, content: `${form.content} {{${variable}}}` })}>{`{{${variable}}}`}</button>)}</div></div>
        <div className="max-w-sm rounded-xl bg-[#e7f7df] p-3 text-sm leading-6 text-gray-800 shadow-inner">{previewLines.map(renderPreviewLine)}<span className="mt-1 block text-right text-[10px] text-gray-400">10:42 ✓✓</span></div>
        <div className="flex flex-wrap gap-2"><button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" />{saving ? 'Salvando...' : selected ? 'Salvar nova versão' : 'Criar template'}</button><button type="button" onClick={openTest} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"><FlaskConical className="h-4 w-4" /> Testar mensagem</button></div>
      </form>
    </div>
    {showTest && <div className="card border-l-4 border-emerald-500 p-5"><div className="mb-3 flex items-center justify-between"><div><h2 className="font-semibold">Teste de mensagem</h2><p className="text-xs text-gray-500">Envie a prévia para um número antes de ativar o template.</p></div><button type="button" title="Fechar teste" aria-label="Fechar teste" onClick={() => setShowTest(false)} className="text-gray-500">×</button></div><form onSubmit={sendTest} className="grid grid-cols-1 gap-3 md:grid-cols-3"><label className="text-xs font-semibold text-gray-600">Conexão<select className="input mt-1" required value={testConnection} onChange={(event) => setTestConnection(event.target.value)}><option value="">Selecione</option>{connections.map((connection) => <option value={connection.id} key={connection.id}>{connection.name} · {connection.status}</option>)}</select></label><label className="text-xs font-semibold text-gray-600">Telefone<input className="input mt-1" required value={testPhone} onChange={(event) => setTestPhone(event.target.value)} placeholder="5531999999999" /></label><button type="submit" disabled={testing || !testConnection} className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Send className="h-4 w-4" />{testing ? 'Enviando...' : 'Enviar teste'}</button></form></div>}
    {selected?.versions?.length > 0 && <div className="card p-5"><div className="mb-3 flex items-center gap-2"><Pencil className="h-5 w-5 text-primary-600" /><h2 className="font-semibold">Histórico de versões</h2></div><div className="space-y-2">{selected.versions.map((version) => <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-100 p-3" key={version.id}><span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold">v{version.version}</span><span className="min-w-0 flex-1 truncate text-sm text-gray-600">{version.content}</span>{version.isActive ? <span className="text-xs font-semibold text-emerald-600">Ativa</span> : <button type="button" onClick={() => activateVersion(version.id)} className="text-xs font-semibold text-primary-600 hover:underline">Ativar versão</button>}</div>)}</div></div>}
  </div>;
}

const endpointGroups = [
  { title: 'Webhook', color: 'blue', items: ['GET /webhook · listar webhooks', 'POST /webhook · registrar URL', 'DELETE /webhook · remover'] },
  { title: 'Envio', color: 'violet', items: ['POST /send · texto, arquivo e mídia', 'POST /senddocument · documento', 'POST /v5/messages/interactive · interativa'] },
  { title: 'Sessão', color: 'emerald', items: ['GET /info · sessão e verificação', 'GET /health · saúde', 'GET /scan e /paircode · pareamento'] },
  { title: 'Contatos', color: 'amber', items: ['POST /isonwhatsapp · validar telefones', 'GET /contacts · listar contatos', 'POST /contact/search · pesquisar'] }
];

function ConnectionSettings() {
  const [connections, setConnections] = useState([]);
  const [form, setForm] = useState({ name: '', baseUrl: 'https://quepasa.cdlitauna.com.br/api', token: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(null);

  const loadConnections = async () => {
    try {
      const response = await api.get('/marketing/provider-connections');
      setConnections(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível carregar as conexões');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadConnections(); }, []);

  const createConnection = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/marketing/provider-connections', form);
      toast.success('Conexão criada com segurança');
      setForm({ name: '', baseUrl: 'https://quepasa.cdlitauna.com.br/api', token: '' });
      loadConnections();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível criar a conexão');
    } finally {
      setSaving(false);
    }
  };

  const action = async (connection, type) => {
    try {
      const response = type === 'test'
        ? await api.post(`/marketing/provider-connections/${connection.id}/test`)
        : type === 'register'
          ? await api.post(`/marketing/provider-connections/${connection.id}/webhook`)
          : type === 'remove'
            ? await api.delete(`/marketing/provider-connections/${connection.id}/webhook`)
            : await api.post(`/marketing/provider-connections/${connection.id}/deactivate`);
      toast.success(type === 'test' ? (response.data?.verified ? 'Sessão verificada e saudável' : 'Conexão respondeu, mas não está verificada') : type === 'register' ? 'Webhook registrado e confirmado' : type === 'remove' ? 'Webhook removido' : 'Conexão desativada');
      loadConnections();
    } catch (error) {
      toast.error(error.response?.data?.message || 'A operação Quepasa falhou');
      loadConnections();
    }
  };

  const statusMeta = {
    active: ['Ativa e verificada', 'bg-emerald-50 text-emerald-700', CheckCircle2],
    unverified: ['Não verificada', 'bg-amber-50 text-amber-700', Activity],
    error: ['Erro de sessão', 'bg-red-50 text-red-700', AlertTriangle],
    inactive: ['Inativa', 'bg-gray-100 text-gray-600', X]
  };

  return <div className="space-y-6">
    <div><p className="text-xs font-bold tracking-widest text-primary-600">MARKETING · CONFIGURAÇÕES</p><h1 className="text-2xl font-bold text-gray-900 dark:text-white">Conexões WhatsApp</h1><p className="mt-1 text-sm text-gray-500">Cadastre múltiplas sessões Quepasa e gerencie o webhook individual de cada conexão.</p></div>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <form onSubmit={createConnection} className="card space-y-4 p-5 xl:col-span-1">
        <div className="flex items-center gap-2"><Plus className="h-5 w-5 text-primary-600" /><h2 className="font-semibold">Nova conexão</h2></div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome da conexão<input className="input mt-1" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ex.: WhatsApp Comercial" /></label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Base URL<input className="input mt-1" required type="url" value={form.baseUrl} onChange={(event) => setForm({ ...form, baseUrl: event.target.value })} /></label>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Token da sessão<input className="input mt-1" required type="password" value={form.token} onChange={(event) => setForm({ ...form, token: event.target.value })} placeholder="X-QUEPASA-TOKEN" /></label>
        <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700"><ShieldCheck className="mr-1 inline h-4 w-4" /> O token é criptografado no backend e nunca é exibido após o cadastro.</div>
        <button type="submit" disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"><Plus className="h-4 w-4" />{saving ? 'Salvando...' : 'Adicionar conexão'}</button>
      </form>
      <div className="space-y-3 xl:col-span-2">
        {loading ? <div className="card p-6 text-sm text-gray-500">Carregando conexões...</div> : connections.length === 0 ? <div className="card flex min-h-64 flex-col items-center justify-center p-8 text-center"><Webhook className="h-10 w-10 text-gray-300" /><h2 className="mt-3 font-semibold text-gray-700 dark:text-gray-200">Nenhuma conexão cadastrada</h2><p className="mt-1 text-sm text-gray-500">Adicione a primeira sessão Quepasa para liberar testes e webhooks dinâmicos.</p></div> : connections.map((connection) => { const [label, color, StatusIcon] = statusMeta[connection.status] || statusMeta.unverified; return <div className="card p-5" key={connection.id}><div className="flex flex-wrap items-start justify-between gap-3"><div className="flex items-start gap-3"><span className={`rounded-lg p-2 ${color}`}><StatusIcon className="h-5 w-5" /></span><div><h2 className="font-semibold text-gray-900 dark:text-white">{connection.name}</h2><p className="mt-1 break-all text-xs text-gray-500">{connection.baseUrl}</p><span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${color}`}>{label}</span></div></div><div className="flex items-center gap-2"><button type="button" title="Testar conexão" aria-label="Testar conexão" onClick={() => action(connection, 'test')} className="rounded-lg bg-cyan-500 p-2 text-white hover:bg-cyan-600"><Activity className="h-4 w-4" /></button><button type="button" title="Registrar ou atualizar webhook" aria-label="Registrar ou atualizar webhook" onClick={() => action(connection, 'register')} className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700"><RefreshCw className="h-4 w-4" /></button>{connection.webhookRegistered && <button type="button" title="Remover webhook" aria-label="Remover webhook" onClick={() => action(connection, 'remove')} className="rounded-lg bg-red-600 p-2 text-white hover:bg-red-700"><Trash2 className="h-4 w-4" /></button>}<button type="button" title="Ver eventos da sessão" aria-label="Ver eventos da sessão" onClick={async () => { try { const response = await api.get(`/marketing/provider-connections/${connection.id}/events`); setSelectedEvents(response.data?.data); } catch (error) { toast.error(error.response?.data?.message || 'Não foi possível consultar eventos'); } }} className="rounded-lg bg-gray-500 p-2 text-white hover:bg-gray-600"><Eye className="h-4 w-4" /></button>{connection.status !== 'inactive' && <button type="button" title="Desativar conexão" aria-label="Desativar conexão" onClick={() => action(connection, 'deactivate')} className="rounded-lg bg-gray-700 p-2 text-white hover:bg-gray-800"><Power className="h-4 w-4" /></button>}</div></div>{connection.webhookUrl && <p className="mt-4 flex items-start gap-2 break-all rounded-lg bg-gray-50 p-3 text-xs text-gray-600"><Link2 className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />{connection.webhookUrl}</p>}<div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-500"><span>WID: <strong className="font-medium text-gray-700">{connection.sessionWid || 'não verificado'}</strong></span><span>Última saúde: <strong className="font-medium text-gray-700">{connection.lastHealthCheckAt ? new Date(connection.lastHealthCheckAt).toLocaleString('pt-BR') : 'pendente'}</strong></span></div></div>; })}
      </div>
    </div>
    {selectedEvents && <div className="card p-5"><div className="mb-3 flex items-center justify-between"><h2 className="font-semibold">Eventos da sessão</h2><button type="button" title="Fechar eventos" aria-label="Fechar eventos" onClick={() => setSelectedEvents(null)} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button></div><pre className="max-h-72 overflow-auto rounded-lg bg-gray-950 p-4 text-xs text-green-300">{JSON.stringify(selectedEvents, null, 2)}</pre></div>}
    <section><div className="mb-3 flex items-center gap-2"><Webhook className="h-5 w-5 text-primary-600" /><div><h2 className="font-semibold text-gray-900 dark:text-white">Sugestão · API Quepasa 5.26.1016.2016</h2><p className="text-xs text-gray-500">Endpoints mapeados para as próximas etapas do motor de campanhas.</p></div></div><div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">{endpointGroups.map((group) => <div className="card p-4" key={group.title}><h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{group.title}</h3><ul className="mt-3 space-y-2">{group.items.map((item) => <li className="text-xs leading-5 text-gray-500" key={item}>{item}</li>)}</ul></div>)}</div></section>
    <div className="card border-l-4 border-amber-400 bg-amber-50 p-4 text-xs text-amber-800"><strong>Regras operacionais:</strong> verificar `/info` e `/health` antes de ativar, responder o webhook imediatamente, criptografar tokens, preservar payloads originais, impedir duplicidade por conexão + evento e suspender envios quando houver `requires_reauth` ou `active_restriction`.</div>
  </div>;
}

export default function Marketing() {
  const { pathname } = useLocation();
  const current = navItems.find((item) => item.path === pathname) || navItems[0];
  const content = pathname === '/marketing/campanhas' ? <CampaignsModule /> : pathname === '/marketing/contatos' ? <MarketingContacts /> : pathname === '/marketing/grupos' ? <MarketingGroups /> : pathname === '/marketing/mensagens' ? <MessageTemplates /> : pathname === '/marketing/relatorios' ? <SimpleModule title="Relatórios" description="Acompanhe entrega, leitura, falhas e eficiência por campanha." icon={BarChart3} /> : pathname === '/marketing/configuracoes' ? <ConnectionSettings /> : <Overview />;
  return <div className="space-y-6"><div className="flex items-center gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">{navItems.map(({ label, path, icon: Icon }) => <Link className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium ${current.path === path ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`} to={path} key={path}><Icon className="h-4 w-4" />{label}</Link>)}</div>{content}</div>;
}
